import {CAMPAIGN_KEYS, useCampaignActiveApplicationsQuery} from "@/api/campaignApi";
import {useInvalidateChatNeedsReply} from "@/api/chatApi";
import {SUBMISSION_KEYS, useAcceptSubmissionMutation} from "@/api/submissionApi";
import AISparkIcon from "@/assets/colorable/ai_spark.svg?react";
import EditIcon from "@/assets/colorable/edit.svg?react";
import InfoIcon from "@/assets/colorable/info.svg?react";
import PlayIcon from "@/assets/colorable/play.svg?react";
import RevisionIcon from "@/assets/colorable/revision.svg?react";
import TickIcon from "@/assets/colorable/tick.svg?react";
import TimesIcon from "@/assets/colorable/times.svg?react";
import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import AIFeedbackPanel from "@/components/ContentApproval/AIFeedbackPanel";
import ContentGuidanceBox from "@/components/ContentApproval/ContentGuidanceBox";
import RevisionForm from "@/components/ContentApproval/RevisionForm";
import {useUserAvatarPlaceholder} from "@/hooks/useUserAvatarPlaceholder";
import {useIsAdmin} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {MARKDOWN_SYMBOLS_PATTERN} from "@/store/campaignForm";
import useCampaignInfluencerFilterStore from "@/store/campaignInfluencerFilter";
import useCampaignManagementStore, {useManagementCampaign} from "@/store/campaignManagement";
import {useCarouselStore} from "@/store/carousel";
import {CONTENT_APPROVAL_MAX_REVISIONS} from "@/store/constants";
import {overrideSubmissionModalAtom, reviewContentModalAtom, revisionAtom} from "@/store/jotaiAtoms";
import type {TContentSubmission} from "@/store/types";
import {CAPTION_LIMIT, getCarouselItemFromSubmission} from "@/utils/campaignUtils";
import {isAiContentApprovalMode} from "@/utils/contentApprovalMode";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useAtom, useSetAtom} from "jotai";
import {countBy, sortBy} from "lodash";
import {useEffect, useRef, useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const ReviewContentModal: FC = () => {
  const UserAvatarPlaceholder = useUserAvatarPlaceholder();
  const {t} = useTranslation(["content_approval", "common"]);
  const campaign = useManagementCampaign();
  const allSubmissionIds = useCampaignManagementStore(state => state.management?.all_content_submission_ids);
  const completedSubmissionIds = useCampaignManagementStore(
    state => state.management?.completed_content_submission_ids,
  );
  const revisionRequestedSubmissionIds = useCampaignManagementStore(
    state => state.management?.revision_requested_content_submission_ids,
  );
  const search = useCampaignInfluencerFilterStore(state => state.filters.search);
  const openCarousel = useCarouselStore(state => state.open);
  const [data, setData] = useAtom(reviewContentModalAtom);
  const [revisions, setRevisions] = useAtom(revisionAtom);
  const isAdmin = useIsAdmin();
  const setOverrideSubmissionModal = useSetAtom(overrideSubmissionModalAtom);
  const [selectedSubmission, setSelectedSubmission] = useState<TContentSubmission>();
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const [isJumped, setIsJumped] = useState(false);
  const queryClient = useQueryClient();
  const invalidateChatNeedsReply = useInvalidateChatNeedsReply();
  const revisionFormRef = useRef<HTMLDivElement>(null);
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const pendingTimestampRef = useRef<number | null>(null);
  const setCarouselValue = useCarouselStore(state => state.setValue);

  const {data: campaignApplications, fetchNextPage} = useCampaignActiveApplicationsQuery({
    campaignId: campaign.id,
    searchParams: "",
  });

  const getCleanSubmissionIds = () => {
    if (!allSubmissionIds) return [];
    if (!completedSubmissionIds) return allSubmissionIds;
    return allSubmissionIds.filter(
      id => !completedSubmissionIds.includes(id) && !revisionRequestedSubmissionIds?.includes(id),
    );
  };

  // The lightbox queue is built from the pending ("clean") submissions. When the modal is viewing a
  // completed/revision submission (e.g. AI auto-approved/auto-rejected), that submission is not in the
  // clean list, so the lightbox shows just that single submission instead of the pending queue.
  const getCarouselSubmissionIds = () => {
    const cleanSubmissionIds = getCleanSubmissionIds();
    if (selectedSubmission && !cleanSubmissionIds.includes(selectedSubmission.id)) {
      return [selectedSubmission.id];
    }
    return cleanSubmissionIds;
  };

  const getSelectedSubmissionCarouselId = () =>
    selectedSubmission
      ? selectedSubmission.status === "PUBLISHED"
        ? selectedSubmission.id
        : 1_000_000_000 + selectedSubmission.id
      : undefined;

  const getSelectedIndex = () => {
    if (!data.submissionId) return;
    return allSubmissionIds?.findIndex(id => id === data.submissionId);
  };

  const getSelectedCleanSubmissionIndex = () => getCleanSubmissionIds().findIndex(id => id === data.submissionId);

  const getIsLastClean = () => getSelectedCleanSubmissionIndex() === getCleanSubmissionIds().length - 1;

  const selectedSubmissionRevision = data.submissionId ? revisions[data.submissionId] : null;

  const getCarouselMedia = () => {
    const carouselSubmissionIds = getCarouselSubmissionIds();
    const media =
      campaignApplications?.flatMap(
        application =>
          application.content_submissions
            ?.filter(submission => carouselSubmissionIds.includes(submission.id))
            .map(submission =>
              getCarouselItemFromSubmission({
                influencer: application.influencer ?? undefined,
                isInstagram: !!campaign.instagram,
                submission,
              }),
            ) ?? [],
      ) ?? [];
    return sortBy(media, item => carouselSubmissionIds.indexOf(item.submissionId!));
  };

  const getSelectedUser = () =>
    selectedSubmission ? getCarouselMedia().find(m => m.id === getSelectedSubmissionCarouselId())?.user : null;

  const selectedUser = getSelectedUser();

  const onClose = () => {
    setCarouselValue("initialTimestamp", null);
    setData(state => {
      state.isOpen = false;
      state.isRevisionFormOpen = false;
      state.showRevisionFormClosingButton = true;
      state.submissionId = null;
    });
  };

  const goToNextOrClose = () =>
    getIsLastClean()
      ? onClose()
      : setData(state => {
          state.submissionId = getCleanSubmissionIds()[getSelectedCleanSubmissionIndex() + 1];
        });

  useEffect(() => {
    setSelectedSlideIndex(0);
    setIsJumped(false);
    pendingTimestampRef.current = null;
    setCarouselValue("initialTimestamp", null);
  }, [data.submissionId]);

  useEffect(() => {
    if (data.isRevisionFormOpen) {
      revisionFormRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
    }
  }, [data.isRevisionFormOpen, selectedSubmission]);

  useEffect(() => {
    setCarouselValue("currentMedia", getCarouselMedia());
  }, [getCarouselMedia]);

  useEffect(() => {
    setCarouselValue("mediaLength", getCarouselSubmissionIds().length);
  }, [getCarouselSubmissionIds]);

  useEffect(() => {
    if (!allSubmissionIds?.length || !data.submissionId) return;

    const cleanSubmissionIds = getCleanSubmissionIds();
    const selectedCleanSubmissionIndex = getSelectedCleanSubmissionIndex();
    const isLastClean = getIsLastClean();
    const nextCleanSubmissionIndex = selectedCleanSubmissionIndex + 1;
    const nextCleanSubmissionId = isLastClean ? null : cleanSubmissionIds[nextCleanSubmissionIndex];

    const isSelectedFetched = campaignApplications?.some(application =>
      application.content_submissions?.some(submission => submission.id === data.submissionId),
    );

    const isNextFetched = isLastClean
      ? true
      : campaignApplications?.some(application =>
          application.content_submissions?.some(submission => submission.id === nextCleanSubmissionId),
        );

    if (!isSelectedFetched || !isNextFetched) {
      fetchNextPage();
    }
  }, [
    allSubmissionIds,
    campaignApplications,
    getCleanSubmissionIds,
    data.submissionId,
    getIsLastClean,
    getSelectedCleanSubmissionIndex,
  ]);

  useEffect(() => {
    const submission = campaignApplications
      ?.flatMap(app => app.content_submissions ?? [])
      .find(sub => sub.id === data.submissionId);
    if (submission) setSelectedSubmission(submission);
  }, [campaignApplications, data.submissionId]);

  useEffect(() => {
    const id = selectedSubmission?.id;
    if (!id) return;
    if (!revisions[id] && selectedSubmission?.caption) {
      setRevisions(state => {
        state[id] = {caption: selectedSubmission.caption ?? "", feedback: ""};
      });
    }
  }, [revisions, selectedSubmission?.caption, selectedSubmission?.id, setRevisions]);

  const {mutate: approveContent, isPending: isApprovePending} = useAcceptSubmissionMutation({
    caption: data.isRevisionFormOpen ? selectedSubmissionRevision?.caption! : undefined,
    id: selectedSubmission?.id!,
    onSuccess: () => goToNextOrClose(),
  });

  const {mutate: requestRevision, isPending: isRevisionRequestPending} = useMutation({
    mutationKey: SUBMISSION_KEYS.mRequestRevision(selectedSubmission?.id!),
    mutationFn: () =>
      client.post(SUBMISSION_KEYS.mRequestRevision(selectedSubmission?.id!)[0], {
        caption: selectedSubmissionRevision?.caption,
        feedback_message: selectedSubmissionRevision?.feedback,
      }),
    onSuccess: () => {
      [
        CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id, search),
        CAMPAIGN_KEYS.qCampaignManagement(campaign.id),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));
      invalidateChatNeedsReply();
      capturePostHogEvent(PostHogEvents.ContentRevisionRequested, {
        campaignId: campaign.id,
        revisionCount: selectedSubmission?.revision_count,
        submissionId: selectedSubmission?.id,
      });
      goToNextOrClose();
    },
  });

  if (!selectedSubmission || getSelectedIndex() === undefined) {
    return null;
  }

  const mediaItems = selectedSubmission.carousel_medias?.length
    ? selectedSubmission.carousel_medias
    : [selectedSubmission as TContentSubmission];
  const isMultiItem = mediaItems.length > 1;
  const isStory = selectedSubmission.content_type === "STORY";
  const isPendingReview = selectedSubmission.status === "PENDING";
  const isSemiAutomatedAi = campaign.content_approval_mode === "semi_automated";
  const canOverride =
    (isAdmin || isSemiAutomatedAi) &&
    (selectedSubmission.status === "REJECTED" || selectedSubmission.status === "REVISION_REQUESTED");
  const isAIFinalRevision =
    isAiContentApprovalMode(campaign.content_approval_mode) &&
    selectedSubmission.ai_evaluation?.recommendation === "REQUEST_REVISION" &&
    selectedSubmission.status !== "REJECTED" &&
    (selectedSubmission.revision_count ?? 0) >= CONTENT_APPROVAL_MAX_REVISIONS;
  const currentMedia = mediaItems[selectedSlideIndex] ?? mediaItems[0];

  const flaggedChecksBySlide = countBy(
    [
      ...(selectedSubmission.ai_evaluation?.check_results.generic ?? []),
      ...(selectedSubmission.ai_evaluation?.check_results.briefing ?? []),
    ].filter(c => !c.passed && c.item_index !== undefined),
    c => c.item_index! - 1,
  );

  return (
    <Modal
      backgroundClassName="max-w-[820px] max-h-[90vh]"
      customTitleRow={
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1.5 p-5">
          <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
            {isAiContentApprovalMode(campaign.content_approval_mode) && (
              <AISparkIcon className="size-4 text-violet-500" />
            )}
            <span className="text-neutral-black text-xl leading-5 font-semibold whitespace-nowrap">
              {t("content_checks")}
            </span>
          </div>
          {selectedUser && (
            <div className="flex shrink-0 items-center gap-2">
              <div className="bg-good-grey-200 size-1 rounded-full" />
              <img className="size-6 shrink-0 rounded-full" src={selectedUser.picture ?? UserAvatarPlaceholder} />
              <span className="text-neutral-black text-sm font-semibold whitespace-nowrap">
                {selectedUser.first_name} {selectedUser.last_name}
              </span>
            </div>
          )}
          <div className="flex shrink-0 items-center gap-2">
            {selectedUser && <div className="bg-good-grey-200 size-1 rounded-full" />}
            <span className="bg-primary-light-solid text-primary-dark-solid rounded-md px-2.5 py-1 text-xs font-semibold whitespace-nowrap">
              {isStory
                ? t("story_badge", {count: mediaItems.length})
                : selectedSubmission.content_type === "REEL"
                  ? t("reel")
                  : selectedSubmission.content_type === "TIKTOK_VIDEO"
                    ? t("tiktok_video")
                    : t("carousel_badge", {count: mediaItems.length})}
            </span>
          </div>
        </div>
      }
      customBottomRow={
        canOverride ? (
          <div className="border-neutral-white-dark flex justify-end border-t p-5">
            <Button
              buttonStyle="Gradient"
              className="min-h-15 px-8 py-4 sm:min-w-46"
              onClick={() => setOverrideSubmissionModal(selectedSubmission.id)}>
              {t("approve")}
            </Button>
          </div>
        ) : isPendingReview ? (
          <div className="border-neutral-white-dark flex flex-col items-end justify-between gap-3 border-t p-5 sm:flex-row md:gap-21.5">
            {data.showRevisionFormClosingButton &&
              selectedSubmission.revision_count !== undefined &&
              selectedSubmission.revision_count < CONTENT_APPROVAL_MAX_REVISIONS && (
                <Button
                  buttonStyle="White"
                  className="text-primary-dark-solid w-full gap-2 px-8 py-6 sm:w-fit"
                  onClick={() =>
                    setData(state => {
                      state.isRevisionFormOpen = !data.isRevisionFormOpen;
                    })
                  }>
                  {data.isRevisionFormOpen ? (
                    <span className="text-danger-medium-red">{t("common:cancel")}</span>
                  ) : (
                    <>
                      <EditIcon />
                      <span>{t("request_revision")}</span>
                    </>
                  )}
                </Button>
              )}
            <div className="ml-auto flex w-full flex-col gap-2 md:w-fit">
              {data.isRevisionFormOpen && !selectedSubmissionRevision?.feedback && (
                <div className="text-neutral-grey-dark flex gap-2">
                  <InfoIcon className="my-1 size-3.5 shrink-0" />
                  <span className="text-xs">{t("send_caption_revision_and_approve_content_disclaimer")}</span>
                </div>
              )}
              <Button
                buttonStyle="Gradient"
                className="min-h-15 w-full px-8 py-4 sm:min-w-46"
                isDisabled={
                  data.isRevisionFormOpen &&
                  (!selectedSubmissionRevision?.caption ||
                    (!selectedSubmissionRevision?.feedback &&
                      selectedSubmissionRevision?.caption === selectedSubmission?.caption) ||
                    (!selectedSubmissionRevision?.feedback && !selectedSubmissionRevision?.caption) ||
                    selectedSubmissionRevision?.caption.replace(MARKDOWN_SYMBOLS_PATTERN, "").length > CAPTION_LIMIT)
                }
                isLoading={isApprovePending || isRevisionRequestPending}
                onClick={() =>
                  data.isRevisionFormOpen && selectedSubmissionRevision?.feedback ? requestRevision() : approveContent()
                }>
                {t(
                  data.isRevisionFormOpen
                    ? !selectedSubmissionRevision?.feedback
                      ? "send_caption_revision_and_approve_content"
                      : selectedSubmission.revision_count === 0
                        ? "send_revisions"
                        : "send_final_revision"
                    : "approve_content",
                )}
              </Button>
            </div>
          </div>
        ) : (
          <></>
        )
      }
      isLoading={isApprovePending || isRevisionRequestPending}
      modalTitle={t("content_checks")}
      onClose={onClose}>
      {allSubmissionIds && allSubmissionIds?.length > 1 && (
        <div className="flex flex-col items-center justify-center gap-3">
          <span className="text-black-bluetint text-xl font-semibold">{`${getSelectedIndex()! + 1}/${allSubmissionIds?.length}`}</span>
          <div className="flex flex-wrap items-center justify-center gap-5">
            {allSubmissionIds?.map((id, index) => {
              const isCompleted = completedSubmissionIds?.includes(id);
              const isRevisionRequested = revisionRequestedSubmissionIds?.includes(id);
              const isSelected = getSelectedIndex() === index;
              return (
                <button
                  key={index}
                  className={clsxm(
                    "bg-primary-light-solid border-neutral-grey-light-dark flex size-5 items-center justify-center rounded-full border",
                    isCompleted && "bg-success-herbal border-success-light-green cursor-default",
                    isRevisionRequested &&
                      "bg-primary-light-solid border-neutral-grey-light-dark cursor-default border",
                    isSelected && "bg-primary-medium-solid border-primary-sky size-5 border-4",
                  )}
                  onClick={() => {
                    if (!isCompleted && !isRevisionRequested) {
                      setData(state => {
                        state.submissionId = id;
                      });
                    }
                  }}>
                  {isSelected ? null : isRevisionRequested ? (
                    <RevisionIcon className="text-neutral-black size-2.5 shrink-0" />
                  ) : isCompleted ? (
                    <TickIcon className="size-2 shrink-0 text-white" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isAIFinalRevision ? (
        <div className="bg-primary-light-solid border-primary-sky flex items-center gap-3 rounded-md border px-5 py-3">
          <AISparkIcon className="text-primary-dark-solid size-3.5 shrink-0" />
          <span className="text-neutral-black text-sm">{t("ai_auto_revision_cta")}</span>
        </div>
      ) : (
        selectedSubmission.revision_count !== undefined &&
        selectedSubmission.revision_count === CONTENT_APPROVAL_MAX_REVISIONS - 1 && (
          <div className="bg-neutral-grey-lightest border-neutral-grey-light-dark flex items-center gap-2 rounded-md border px-5 py-3">
            <InfoIcon className="text-neutral-grey-dark size-3.5 shrink-0" />
            <span className="text-neutral-black text-sm font-bold">{t("last_revision_info")}</span>
          </div>
        )
      )}

      {selectedSubmission.ai_evaluation &&
        (() => {
          const allChecks = [
            ...selectedSubmission.ai_evaluation!.check_results.generic,
            ...selectedSubmission.ai_evaluation!.check_results.briefing,
          ];
          const passedCount = allChecks.filter(c => c.passed).length;
          const flaggedCount = allChecks.length - passedCount;
          const totalCount = allChecks.length;
          return (
            <div className="flex flex-wrap items-center gap-2">
              {isAiContentApprovalMode(campaign.content_approval_mode) && (
                <span className="text-2xs flex shrink-0 items-center gap-1.5 font-semibold text-violet-500">
                  <AISparkIcon className="size-3 shrink-0" />
                  {t("automated_review")}
                </span>
              )}
              <div className="border-success-mint-green bg-success-light-green text-success-medium-green flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold">
                <TickIcon className="size-3" />
                {passedCount} {t("checks_passed")}
              </div>
              {flaggedCount > 0 && (
                <div className="border-danger-rubicund bg-danger-light-red text-danger-dark-red flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold">
                  <TimesIcon className="size-3" />
                  {flaggedCount} {t("checks_failed")}
                </div>
              )}
              <span className="text-2xs text-neutral-grey-dark ml-auto font-mono">
                {totalCount} {t("checks_run")}
              </span>
            </div>
          );
        })()}

      <div
        className={clsxm(
          "flex flex-col gap-5",
          (!!selectedSubmission.ai_evaluation || data.isRevisionFormOpen) && "md:flex-row",
        )}>
        <div
          ref={mediaContainerRef}
          className={clsxm(
            "flex flex-col gap-3",
            selectedSubmission.ai_evaluation || data.isRevisionFormOpen
              ? "items-center sm:mx-auto sm:w-64 sm:items-start md:sticky md:top-0 md:mx-0 md:w-52 md:shrink-0 md:self-start"
              : "mx-auto w-full max-w-72 items-start",
          )}>
          {isMultiItem && (
            <div className="scrollbar-hide flex w-full gap-1.5 overflow-x-auto pt-2 pb-0.5">
              {mediaItems.map((_, index) => {
                const flaggedCount = flaggedChecksBySlide[index] ?? 0;
                return (
                  <div key={index} className="relative shrink-0">
                    <button
                      className={clsxm(
                        "rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors",
                        selectedSlideIndex === index
                          ? "bg-primary-dark-solid border-transparent text-white"
                          : "border-neutral-grey-light-dark text-neutral-grey-dark hover:text-neutral-black bg-white hover:border-violet-200",
                      )}
                      onClick={() => {
                        setIsJumped(false);
                        pendingTimestampRef.current = null;
                        setSelectedSlideIndex(index);
                      }}>
                      {isStory ? `${t("story")} ${index + 1}` : `${t("slide")} ${index + 1}`}
                    </button>
                    {flaggedCount > 0 && (
                      <div className="bg-danger-raspberry text-2xs absolute -top-1 -right-1 flex size-3.5 items-center justify-center rounded-full leading-none font-bold text-white">
                        {flaggedCount}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <button
            className="bg-neutral-black group relative aspect-3/4 w-full overflow-hidden rounded-xl"
            onMouseEnter={() => {
              if (!isJumped) videoRef.current?.play().catch(() => {});
            }}
            onMouseLeave={() => {
              if (!isJumped && videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
              }
            }}
            onClick={() => {
              setCarouselValue("defaultAlbumItem", {...currentMedia, media_type: currentMedia.media_type ?? "IMAGE"});
              openCarousel({
                campaign: {id: campaign.id, title: campaign.title!},
                initialMediaId: getSelectedSubmissionCarouselId(),
                media: getCarouselMedia(),
                mediaLength: getCarouselSubmissionIds().length,
                onChange: index => {
                  setCarouselValue("defaultAlbumItem", null);
                  setData(state => {
                    state.submissionId = getCarouselSubmissionIds()[index];
                  });
                },
                shouldUsePersistedIndex: true,
                showCaption: false,
                showFooter: false,
                variant: "content_approval",
              });
            }}>
            {currentMedia.media_type === "VIDEO" && currentMedia.media_file ? (
              <>
                <video
                  ref={videoRef}
                  className="absolute inset-0 h-full w-full object-cover"
                  loop
                  muted
                  onLoadedMetadata={() => {
                    if (pendingTimestampRef.current !== null && videoRef.current) {
                      videoRef.current.currentTime = pendingTimestampRef.current;
                      videoRef.current.play().catch(() => {});
                      pendingTimestampRef.current = null;
                    }
                  }}
                  playsInline
                  poster={currentMedia.thumbnail_file ?? undefined}
                  preload="metadata"
                  src={currentMedia.media_file}
                />
                {!isJumped && (
                  <div className="absolute inset-0 flex items-center justify-center transition-opacity group-hover:opacity-0">
                    <div className="flex size-10 items-center justify-center rounded-full border border-black/80 bg-black/60">
                      <PlayIcon className="h-4 w-3 text-white" />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <img
                draggable={false}
                src={currentMedia.thumbnail_file ?? undefined}
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}
          </button>

          <ContentGuidanceBox
            className="min-h-none max-h-none gap-3 rounded-xl border border-none p-3"
            description={selectedSubmission?.caption}
            descriptionClassName="p-0 rounded-xl"
            influencerImage={selectedUser?.picture ?? UserAvatarPlaceholder}
            title={t("caption")}
            titleClassName="min-h-fit p-0 text-neutral-black rounded-xl"
          />
        </div>

        {(selectedSubmission.ai_evaluation || data.isRevisionFormOpen) && (
          <div className="flex min-w-0 flex-1 flex-col gap-5">
            {selectedSubmission.ai_evaluation && (
              <AIFeedbackPanel
                aiEvaluation={selectedSubmission.ai_evaluation}
                onJump={(slideIndex, timestampSeconds) => {
                  if (timestampSeconds !== undefined) {
                    setCarouselValue("initialTimestamp", timestampSeconds);
                    pendingTimestampRef.current = timestampSeconds;
                    if (slideIndex === selectedSlideIndex && videoRef.current) {
                      videoRef.current.currentTime = timestampSeconds;
                      videoRef.current.play().catch(() => {});
                      pendingTimestampRef.current = null;
                    }
                  }
                  setIsJumped(true);
                  setSelectedSlideIndex(slideIndex);
                  mediaContainerRef.current?.scrollIntoView({behavior: "smooth", block: "nearest"});
                }}
              />
            )}
            {data.isRevisionFormOpen && (
              <div ref={revisionFormRef}>
                <div className="border-neutral-grey-light-dark mb-5 border-t" />
                <RevisionForm id={selectedSubmission.id} type={selectedSubmission?.media_type ?? "IMAGE"} />
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReviewContentModal;
